// PageHeader — unified page header.
//
// Three competing header styles existed across the app (plain <h1>, the
// PhaseTabs header, ad-hoc eyebrow+title blocks). This consolidates them
// into one. The markup is lifted from the PhaseTabs header so phase pages
// and plain pages render identically.
//
// Usage:
//   <PageHeader
//     eyebrow="GOVERNANCE"
//     title="BOD Brief"
//     subtitle="Board-ready summary"
//     headerRight={<LiveIndicator />}
//   />

import type { ReactNode } from "react";

interface PageHeaderProps {
  /** Short uppercase section label shown above the title. */
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned header content (e.g. LiveIndicator, action buttons). */
  headerRight?: ReactNode;
}

export default function PageHeader({
  eyebrow,
  title,
  subtitle,
  headerRight,
}: PageHeaderProps) {
  return (
    <header className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        {eyebrow && (
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
            {eyebrow}
          </p>
        )}
        <h1 className="text-2xl font-black text-theme-primary tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="text-theme-secondary mt-1 text-sm">{subtitle}</p>
        )}
      </div>
      {headerRight && <div className="shrink-0">{headerRight}</div>}
    </header>
  );
}
